import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import './PricingEditors.css';
const URL = '/admin/engine/tariff';
const OPEN = 1e9;
const number = value => String(value).trim() ? Number(String(value).replace(',', '.')) : NaN;
const range = (rows, i) => `${i ? `Über ${rows[i - 1].max || '…'}` : 'Ab 0'} ${i === rows.length - 1 ? 'km · offen' : `bis ${rows[i].max || '…'} km`}`;
const euro = value => Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const km = value => Number(value).toLocaleString('de-DE', { maximumFractionDigits: 3 });

export default function TariffEditor({ onApplied }) {
  const [loaded, setLoaded] = useState(null), [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false), [review, setReview] = useState(false), [error, setError] = useState(''), [saved, setSaved] = useState(false);
  // TARIF-KLEMME-001 Teil C: Der Server meldet Rückwärtssprünge der Grundgebühr in `warnings` — bisher las
  // diese Seite das Feld gar nicht, der Hinweis war also unsichtbar. Er kommt aus JEDER Antwort (Lesen wie
  // Speichern) und bleibt deshalb auch nach „Neu laden“ stehen. Bearbeiten nimmt ihn weg, wie auf dem
  // Zeitregel-Bildschirm: der Entwurf kann den Sprung ja gerade heilen.
  const [warnings, setWarnings] = useState([]);
  const flight = useRef(false);
  const adopt = data => {
    if (!Array.isArray(data?.sections) || !data.sections.length || !/^[a-f0-9]{64}$/.test(data.revision || '')) throw new Error('unreadable_tariff');
    setLoaded(data); setRows(data.sections.map(row => Object.fromEntries(Object.entries(row).map(([k, v]) => [k, String(v)])))); setReview(false);
    setWarnings(Array.isArray(data?.warnings) ? data.warnings.filter(w => w && w.code === 'distance_base_band_drops') : []);
  };
  useEffect(() => {
    let cancelled = false;
    api.get(URL).then(({ data }) => { if (!cancelled) adopt(data); }).catch(() => { if (!cancelled) setError('Grundtarif konnte nicht geladen werden.'); });
    return () => { cancelled = true; };
  }, []);
  const payload = rows?.map(row => Object.fromEntries(Object.entries(row).map(([key, value]) => [key, number(value)])));
  const valid = payload?.every((row, i) => ['base', 'perKm', 'perMin'].every(k => Number.isFinite(row[k]) && row[k] >= 0)
    && Number.isFinite(row.max) && row.max > (i ? payload[i - 1].max : 0)) && payload[payload.length - 1].max === OPEN;
  const changed = payload && JSON.stringify(payload) !== JSON.stringify(loaded.sections);
  const edit = (i, key, value) => { setSaved(false); setWarnings([]); setRows(current => current.map((row, j) => j === i ? { ...row, [key]: value } : row)); };
  const reload = async () => {
    if (flight.current) return;
    flight.current = true; setBusy(true);
    try { const { data } = await api.get(URL); adopt(data); setError(''); setSaved(false); }
    catch { setError('Aktueller Stand nicht lesbar; Entwurf erhalten.'); }
    finally { flight.current = false; setBusy(false); }
  };
  const save = async () => {
    if (flight.current || !valid || !review) return;
    flight.current = true; setBusy(true); setError('');
    try {
      const { data } = await api.post(URL, { sections: payload, expected_revision: loaded.revision, confirm: 'APPLY-TARIFF' });
      adopt(data); onApplied?.(data.config); setSaved(true);
    } catch (e) {
      setReview(false); setError(e?.response?.status === 409 ? 'Grundtarif inzwischen geändert. Ihr Entwurf bleibt erhalten; bitte den aktuellen Stand bewusst neu laden.'
        : e?.response?.data?.message || 'Speichern fehlgeschlagen. Ihr Entwurf bleibt erhalten.');
    } finally { flight.current = false; setBusy(false); }
  };
  return <section className="pricing-editor" aria-label="Grundtarif">
    <h3>Grundtarif · Taxameter</h3>
    <p>Die Gesamtdistanz bestimmt genau eine Grundgebühr. Kilometer und Minuten werden wie bisher abschnittsweise berechnet.</p>
    {error && <p role="alert" className="pricing-error">{error}</p>}
    {saved && <p role="status">Grundtarif gespeichert. Bereits gebundene Preise bleiben erhalten.</p>}
    {warnings.map((w, i) => <p key={i} role="status" className="pricing-error" data-testid="tarif-band-warning">
      Hinweis: Die Grundgebühr springt bei {km(w.at_km)} km von {euro(w.base_before)} € auf {euro(w.base_after)} € zurück
      ({euro(w.drop_eur)} € weniger). Gespeichert — im Preis hält die Tarif-Klemme die längere Fahrt auf dem
      Preis der kürzeren, der Rückwärtssprung erreicht den Kunden also nicht.</p>)}
    {!rows ? <button type="button" disabled={busy} onClick={reload}>Grundtarif laden</button> : <>
      <fieldset disabled={busy || review}>
        {rows.map((row, i) => <div className="tariff-row" key={i} data-testid={`tarif-abschnitt-${i + 1}`}>
          <div className="tariff-range"><strong>Abschnitt {i + 1}</strong><span>{range(rows, i)}</span>
            {i < rows.length - 1 && <label>Bis km<input aria-label={`Grenze Abschnitt ${i + 1}`} inputMode="decimal" value={row.max} onChange={e => edit(i, 'max', e.target.value)} /></label>}
          </div>
          {[['base', 'Grundgebühr €'], ['perKm', '€/km'], ['perMin', '€/Minute']].map(([key, label]) =>
            <label key={key}>{label}<input aria-label={`Abschnitt ${i + 1} ${label}`} inputMode="decimal" value={row[key]} onChange={e => edit(i, key, e.target.value)} /></label>)}
        </div>)}
        <div className="pricing-actions">
          <button type="button" disabled={!valid || rows.length >= 63} onClick={() => { setSaved(false); setRows(current => {
            const next = current.map(row => ({ ...row })), last = next[next.length - 1];
            last.max = String((next.length > 1 ? number(next[next.length - 2].max) : 0) + 5);
            return [...next, { ...last, max: String(OPEN) }];
          }); }}>Abschnitt hinzufügen</button>
          <button type="button" disabled={rows.length <= 1} onClick={() => { setSaved(false); setRows(current => current.slice(0, -1).map((row, i) => i === current.length - 2 ? { ...row, max: String(OPEN) } : row)); }}>Letzten Abschnitt entfernen</button>
        </div>
      </fieldset>
      {!valid && <p role="alert" className="pricing-error">Alle Beträge ausfüllen; Abschnittsgrenzen müssen positiv und aufsteigend sein.</p>}
      <div className="pricing-actions">
        <button type="button" disabled={busy || review} onClick={reload}>Neu laden und Entwurf verwerfen</button>
        <button type="button" className="pricing-primary" disabled={busy || review || !valid || !changed} onClick={() => setReview(true)}>Grundtarif speichern</button>
      </div>
      {review && <div className="pricing-confirm" role="dialog" aria-label="Grundtarif bestätigen">
        <h4>Grundtarif für neue Angebote übernehmen?</h4>
        <p>Grundgebühren, Abschnittsgrenzen und Sätze werden gemeinsam gespeichert. Bereits gebundene Preise bleiben erhalten.</p>
        <ul>{payload.map((row, i) => <li key={i}>{range(payload, i)}: {row.base} € Grundgebühr · {row.perKm} €/km · {row.perMin} €/Minute</li>)}</ul>
        <div className="pricing-actions"><button type="button" disabled={busy} onClick={() => setReview(false)}>Abbrechen</button>
          <button type="button" className="pricing-primary" disabled={busy} onClick={save}>{busy ? 'Wird gespeichert …' : 'Grundtarif verbindlich übernehmen'}</button></div>
      </div>}
    </>}
  </section>;
}
